// components/AnalysisDisplay.jsx (Modernized)
import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { Button } from 'ui';
import { Scrollbars } from 'react-custom-scrollbars';
import { useToasts } from 'ui/toast';
import FilterComponent from './FilterComponent';
import Excerpt from './Excerpt';
import PageLoadingComponent from './PageLoadingComponent';
import SortDropdown from './filters/SortDropdown';
import { useFilterState } from 'contexts/FilterStateContext';
import { useFilteredExcerpts } from 'hooks/excerpts/queries';
import CodeableContext from 'contexts/CodeableContext';

const AnalysisDisplay = ({
  onExcerptClicked,
  hasEditPermission,
  projectID
}) => {
  // Get toast functionality
  const { addToast } = useToasts();
  
  // Get everything we need from CodeableContext
  const { 
    selectedExcerptId: selectedExcerptID, 
    setSelectedExcerptVisible,
    onDeselect 
  } = useContext(CodeableContext);
  
  // Get filter state from the lightweight context
  const { filters, setSort } = useFilterState();
  
  // Now fetch the filtered excerpts data only in this component
  // Pass 'snippets' as the source since AnalysisDisplay is for snippet analysis
  const {
    excerptIds,
    totalCount,
    isLoading,
    isFetching,
    hasNextPage,
    loadMore,
    error
  } = useFilteredExcerpts(projectID, filters, 'snippets');
  
  const filterRef = useRef(null);

  const handleShowMore = () => {
    if (hasNextPage && !isFetching) {
      loadMore();
    }
  };

  // Stable identity matters: Excerpt is React.memo and this handler goes to
  // every card on the page. A fresh function per render re-rendered all of
  // them (hundreds of cards, thousands of components) on every selection.
  const handleExcerptClick = useCallback((excerptId) => {
    onExcerptClicked(excerptId);
  }, [onExcerptClicked]);

  // Clear selection when project changes
  useEffect(() => {
    onDeselect();
  }, [projectID, onDeselect]);

  // Handle excerpt visibility - now properly implemented
  useEffect(() => {
    if (selectedExcerptID) {
      // Check if the selected excerpt exists in the current filtered list
      const selectedExcerpt = excerptIds.find(excerptId => 
        String(excerptId) === String(selectedExcerptID)
      );
      
      // Update the visibility in the CodeableContext
      const isVisible = !!selectedExcerpt;
      setSelectedExcerptVisible(isVisible);
    } else {
      // If there's no selected excerpt, explicitly set visibility to false
      setSelectedExcerptVisible(false);
    }
  }, [excerptIds, selectedExcerptID, setSelectedExcerptVisible]);

  // Handle errors with toast notification
  useEffect(() => {
    if (error) {
      addToast('Something went wrong. Please refresh and try again.', { 
        appearance: 'error',
        autoDismiss: true
      });
    }
  }, [error, addToast]);

  const filterHeight = filterRef.current?.clientHeight || 100;
  const excerptsExist = excerptIds.length > 0;

  const excerptParagraphs = excerptIds.map((excerptId) => (
    <Excerpt
      key={excerptId}
      excerptId={excerptId}
      selected={excerptId === parseInt(selectedExcerptID)}
      onTextClicked={handleExcerptClick}
      hasEditPermission={hasEditPermission}
    />
  ));

  let loadingText = "";
  if (isLoading && !excerptsExist) {
    loadingText = "";
  } else if (totalCount > 0) {
    loadingText = `${totalCount} Snippets`;
  } else if (totalCount === 0) {
    loadingText = "No Snippets Found";
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "10px 30px" }} ref={filterRef}>
        <p style={{ margin: 0, marginBottom: '10px' }}><strong>Filter snippets by:</strong></p>
        <FilterComponent
          projectID={projectID}
        />
      </div>

      <div style={{ flex: 1, overflow: "hidden" }}>
        <Scrollbars autoHide style={{ height: "100%" }}>
          {(excerptsExist || !isLoading) && (
            <div onClick={onDeselect} style={{ marginLeft: 30, marginRight: 30 }} className='analysis-display'>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <p style={{ margin: 0 }}>{loadingText}</p>
                <SortDropdown value={filters.sort || 'newest_activity'} onChange={setSort} />
              </div>
              {excerptParagraphs}
              {hasNextPage && (
                <div style={{ textAlign: 'center', marginBottom: "20px" }}>
                  <Button
                    variant="white"
                    onClick={handleShowMore}
                    disabled={isFetching}
                  >
                    {isFetching ? 'Loading...' : 'Show More'}
                  </Button>
                </div>
              )}
            </div>
          )}
          {isLoading && !excerptsExist && <PageLoadingComponent />}
        </Scrollbars>
      </div>
    </div>
  );
};

export default AnalysisDisplay;